import classNames from 'classnames';
import { ButtonProps, ButtonSize, ButtonVariant } from './Button';

type variantBasedClassName = { [key in ButtonVariant]?: string };
type IncludesIcon = { includesIcon: boolean };
type UseButtonClassNames = (
  props: Pick<ButtonProps, 'variant' | 'size' | 'className' | 'loading' | 'asSkeleton'> & IncludesIcon,
) => string;

const useButtonClassNames: UseButtonClassNames = ({
  variant,
  size,
  className = '',
  includesIcon,
  loading,
  asSkeleton = false,
}) => {
  const sizeClassNames: { [key in ButtonSize]: string } = {
    xs: 'p-2',
    s: 'py-3 px-6',
    m: 'py-3 px-9',
    l: 'py-3 px-12',
    fit: 'p-0',
    full: 'py-3 w-full',
    icon: 'size-[56px] grid items-center justify-center',
  };

  const secondaryClassName = classNames(
    'bg-white-100 text-gray-700 bg-white rounded-md border border-secondary-black',
    'hover:shadow-bottom',
    'active:outline-2 active:shadow-button active:disabled:shadow-none active:outline-1 active:outline-gray-300 active:outline-offset-0',
    'focus-visible:outline focus-visible:border-white-100 focus-visible:border focus-visible:outline-offset-[3px]',
    'disabled:text-neutral-100 disabled:bg-neutral-400',
  );

  const warningClassName = classNames(
    'bg-red-500 text-neutral-100 outline-offset-0',
    'hover:bg-red-600',
    'active:shadow-button active:disabled:shadow-none active:bg-red-500',
    'focus-visible:outline focus-visible:bg-red-700 focus-visible:outline-gray-700 focus-visible:border-red-700 focus-visible:border focus-visible:outline-offset-[3px]',
    'disabled:bg-neutral-400 disabled:text-neutral-100',
  );

  const underlineClassName = 'pb-[2px] text-gray-700 hover:underline disabled:text-neutral-400';

  const ghostClassName = 'disabled:text-neutral-400';

  const variantClassNames: variantBasedClassName = {
    secondary: secondaryClassName,
    underlined: underlineClassName,
    ghost: ghostClassName,
    warning: warningClassName,
  };

  const buttonClassName =
    variant !== 'buttonLink' &&
    classNames(
      sizeClassNames[size as ButtonSize],
      { 'flex gap-3 items-center': includesIcon },
      { 'cursor-not-allowed': !!loading },
      { relative: asSkeleton && (variantClassNames[variant as ButtonVariant] as string) },
      className,
    );

  // Gruenland:
  return classNames(
    {
      'relative text-[18px]/[22.68px] transition overflow-hidden font-semibold hover:shadow-button inline-flex items-center justify-center rounded-full':
        variant !== 'buttonLink',
    },
    {
      'bg-primary-green text-white hover:bg-primary-green-mid': variant === 'buttonPrimaryGreen',
      'bg-white text-neutrals-black hover:text-primary-green-mid': variant === 'buttonPrimaryNegative',
      'bg-neutrals-black text-white hover:bg-primary-green-mid': variant === 'buttonPrimaryBlack',
      'bg-white text-neutrals-black ring-1 ring-inset ring-neutrals-black hover:bg-primary-green-mid hover:text-white':
        variant === 'buttonSecondary',
      'bg-neutrals-black text-white ring-1 ring-inset ring-white hover:bg-primary-green-mid hover:ring-0':
        variant === 'buttonSecondaryNegative',
      'text-primary-green hover:text-primary-green-mid underline': variant === 'buttonLink',
      [warningClassName]: variant === 'warning',
    },
    buttonClassName,
  );
};

export default useButtonClassNames;
